/**
 * The composer every conversation types into.
 *
 * One box, whichever page: the text, a `+` menu for files and the three web
 * modes, chips for what is attached, a model picker slot, and send. The Chat
 * page and every agent's page render this same component, so learning one is
 * learning the other — and an agent's turn can attach a file, pick a model or
 * search the web exactly the way a chat turn can.
 *
 * It owns no data. Who answers, what was attached and what the modes mean are
 * the page's business; this only asks and shows.
 */

import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useDismiss } from './ModelPicker'
import './composer.css'

/** How a turn is answered — see the backend's `ChatMode` / `TURN_MODE`. */
export type ChatMode = 'chat' | 'web' | 'deep' | 'research'

/** A file read by `POST /brain/chat/files`; the text travels with the turn. */
export interface Attachment {
  name: string
  kind: string
  size: number
  text: string
  chars: number
  truncated: boolean
  pages: number | null
}

export const MODES: { id: ChatMode; label: string; icon: string; hint: string }[] = [
  { id: 'web', label: 'Web search', icon: '◎', hint: 'One quick search, a few pages' },
  { id: 'deep', label: 'Deep search', icon: '◉', hint: 'Reads the best pages in full' },
  { id: 'research', label: 'Research', icon: '✦', hint: 'Plans several searches, writes a report' },
]

export interface ComposerProps {
  /** Which way the menus open: down in the hero, up from the dock. */
  drop: 'up' | 'down'
  draft: string
  onDraft: (v: string) => void
  onSend: () => void
  disabled: boolean
  pending: boolean
  placeholder: string
  inputRef: React.RefObject<HTMLTextAreaElement>
  attachments: Attachment[]
  reading: number
  onRemove: (name: string) => void
  onFiles: (files: FileList | File[]) => void
  mode: ChatMode
  onMode: (m: ChatMode) => void
  canSearch: boolean
  searchChoices: { id: string; label: string; sub: string }[]
  searchVia: string
  onSearchVia: (id: string) => void
  searchConnectorId: string
  modelPicker: ReactNode
  /**
   * A section the page adds to the + menu, after files and before search —
   * the agent page puts its knowledge-graph picker here. Rendered inside the
   * menu, so use the `ch-panelhead` / `ch-item` vocabulary.
   */
  menuExtra?: ReactNode
  /** What the + button is for, in words; the default names files and search. */
  menuTitle?: string
}

export function Composer(p: ComposerProps) {
  const [dragging, setDragging] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)
  const activeMode = MODES.find((m) => m.id === p.mode)

  // Grow with the text, up to a point, rather than making someone drag a
  // corner to see what they wrote.
  useLayoutEffect(() => {
    const el = p.inputRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 220)}px`
  }, [p.draft, p.inputRef])

  return (
    <div
      className="ch-composer"
      data-drop={p.drop}
      data-dragging={dragging || undefined}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes('Files')) {
          e.preventDefault()
          setDragging(true)
        }
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDragging(false)
        if (e.dataTransfer.files.length) p.onFiles(e.dataTransfer.files)
      }}
    >
      <input
        ref={fileInput}
        type="file"
        multiple
        hidden
        accept=".txt,.md,.markdown,.csv,.tsv,.json,.yaml,.yml,.xml,.html,.htm,.pdf,.docx,.py,.js,.ts,.tsx,.jsx,.java,.go,.rs,.rb,.sql,.sh,.log,.rst,.toml,.ini,.cfg,text/*,application/pdf,application/json"
        onChange={(e) => {
          if (e.target.files?.length) p.onFiles(e.target.files)
          e.target.value = ''
        }}
      />

      {(p.attachments.length > 0 || p.reading > 0) && (
        <div className="ch-files">
          {p.attachments.map((a) => (
            <span className="ch-filechip" key={a.name} title={fileTitle(a)}>
              <FileIcon />
              <span className="truncate">{a.name}</span>
              <span className="ch-filemeta">{fileMeta(a)}</span>
              <button
                type="button"
                className="ch-fileremove"
                aria-label={`Remove ${a.name}`}
                onClick={() => p.onRemove(a.name)}
              >
                ×
              </button>
            </span>
          ))}
          {p.reading > 0 && (
            <span className="ch-filechip ch-filereading">
              <Dots /> reading {p.reading === 1 ? 'file' : `${p.reading} files`}…
            </span>
          )}
        </div>
      )}

      <textarea
        ref={p.inputRef}
        className="ch-input"
        rows={1}
        value={p.draft}
        placeholder={p.placeholder}
        disabled={p.disabled || p.pending}
        onChange={(e) => p.onDraft(e.target.value)}
        onPaste={(e) => {
          const files = Array.from(e.clipboardData.files ?? [])
          if (files.length) {
            e.preventDefault()
            p.onFiles(files)
          }
        }}
        onKeyDown={(e) => {
          // Enter sends, shift+Enter is a newline — the convention every chat
          // box already taught everyone.
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            p.onSend()
          }
        }}
      />

      <div className="ch-tools">
        <PlusMenu
          onAddFiles={() => fileInput.current?.click()}
          mode={p.mode}
          onMode={p.onMode}
          canSearch={p.canSearch}
          searchChoices={p.searchChoices}
          searchVia={p.searchVia}
          onSearchVia={p.onSearchVia}
          searchConnectorId={p.searchConnectorId}
          extra={p.menuExtra}
          title={p.menuTitle}
        />

        {activeMode && (
          <button
            type="button"
            className="ch-modechip"
            title={`${activeMode.hint}. Click to turn off.`}
            onClick={() => p.onMode('chat')}
          >
            <span aria-hidden="true">{activeMode.icon}</span>
            {activeMode.label}
            <span className="ch-modechip-x" aria-hidden="true">
              ×
            </span>
          </button>
        )}

        <span className="cp-spacer" />

        {p.modelPicker}

        <button
          type="button"
          className="ch-send"
          aria-label="Send"
          title="Send (Enter)"
          onClick={p.onSend}
          disabled={!p.draft.trim() || p.disabled || p.pending || p.reading > 0}
        >
          {p.pending ? <Dots /> : '↑'}
        </button>
      </div>

      {dragging && <div className="ch-dropzone">Drop files to attach</div>}
    </div>
  )
}

export function fileMeta(a: Attachment): string {
  const parts = [a.kind.toUpperCase()]
  if (a.pages) parts.push(`${a.pages} page${a.pages === 1 ? '' : 's'}`)
  parts.push(a.chars >= 1000 ? `${Math.round(a.chars / 1000)}k chars` : `${a.chars} chars`)
  if (a.truncated) parts.push('cut')
  return parts.join(' · ')
}

export function fileTitle(a: Attachment): string {
  return a.truncated
    ? `${a.name} — only the first ${a.chars.toLocaleString()} characters were kept.`
    : a.name
}

// --- the + menu --------------------------------------------------------------------

function PlusMenu({
  onAddFiles,
  mode,
  onMode,
  canSearch,
  searchChoices,
  searchVia,
  onSearchVia,
  searchConnectorId,
  extra,
  title,
}: {
  onAddFiles: () => void
  mode: ChatMode
  onMode: (m: ChatMode) => void
  canSearch: boolean
  searchChoices: { id: string; label: string; sub: string }[]
  searchVia: string
  onSearchVia: (id: string) => void
  searchConnectorId: string
  extra?: ReactNode
  title?: string
}) {
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  useDismiss(root, open, () => setOpen(false))

  return (
    <div className="ch-menu" ref={root}>
      <button
        type="button"
        className="ch-plus"
        aria-label={title ?? 'Add files or choose a search mode'}
        aria-haspopup="menu"
        aria-expanded={open}
        title={title ?? 'Files, web search, deep search, research'}
        onClick={() => setOpen((v) => !v)}
      >
        <span aria-hidden="true">+</span>
      </button>

      {open && (
        <div className="ch-panel" role="menu">
          <button
            type="button"
            className="ch-item"
            role="menuitem"
            onClick={() => {
              setOpen(false)
              onAddFiles()
            }}
          >
            <span className="ch-itemicon" aria-hidden="true">
              <FileIcon />
            </span>
            <span className="ch-itemtext">
              <span>Add files</span>
              <span className="ch-itemsub">Text, Markdown, CSV, JSON, code, PDF, Word</span>
            </span>
          </button>

          {extra}

          <div className="ch-panelhead">Search the web</div>
          {MODES.map((m) => (
            <button
              key={m.id}
              type="button"
              className="ch-item"
              role="menuitemradio"
              aria-checked={mode === m.id}
              disabled={!canSearch}
              onClick={() => {
                onMode(mode === m.id ? 'chat' : m.id)
                setOpen(false)
              }}
            >
              <span className="ch-itemicon" aria-hidden="true">
                {m.icon}
              </span>
              <span className="ch-itemtext">
                <span>{m.label}</span>
                <span className="ch-itemsub">{m.hint}</span>
              </span>
              {mode === m.id && (
                <span className="ch-check" aria-hidden="true">
                  ✓
                </span>
              )}
            </button>
          ))}

          {canSearch ? (
            searchChoices.length > 1 && (
              <>
                <div className="ch-panelhead">Search with</div>
                {searchChoices.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className="ch-item ch-item-sm"
                    role="menuitemradio"
                    aria-checked={searchVia === c.id}
                    onClick={() => onSearchVia(c.id)}
                  >
                    <span className="ch-itemtext">
                      <span>{c.label}</span>
                      <span className="ch-itemsub">{c.sub}</span>
                    </span>
                    {searchVia === c.id && (
                      <span className="ch-check" aria-hidden="true">
                        ✓
                      </span>
                    )}
                  </button>
                ))}
              </>
            )
          ) : (
            <p className="ch-panelnote">
              Nothing can search yet.{' '}
              <Link to={`/connections/new/${searchConnectorId}`} onClick={() => setOpen(false)}>
                Connect Tavily
              </Link>{' '}
              with your key, or set <code>TAVILY_API_KEY</code> on the API.
            </p>
          )}
        </div>
      )}
    </div>
  )
}

// --- bits ---------------------------------------------------------------------------

export function Dots() {
  return (
    <span className="ch-dots" aria-hidden="true">
      <i />
      <i />
      <i />
    </span>
  )
}

export function FileIcon() {
  return (
    <svg className="ch-fileicon" viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
      <path
        d="M4 1.5h5l3 3v10H4z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <path d="M9 1.5v3h3" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    </svg>
  )
}
